import { FC } from 'react';
import { PhoneNumber } from 'src/apis/organisation-registration/types';
import { Contact } from 'src/apis/organisation-service/types';
import { ContactsRequest } from 'src/apis/organisation-service/use-contacts';
import { Button } from 'src/components/buttons-and-actions/button';
import { SplitActions } from 'src/components/buttons-and-actions/split-actions';
import { Card } from 'src/components/data-display/card';
import { EmptyCard } from 'src/components/data-display/empty-card';
import { EmptyValue } from 'src/components/data-display/empty-value';
import { PageHeader } from 'src/components/layout/page-header';
import { Stack } from 'src/components/layout/stack';
import { ExternalLink, Link } from 'src/components/navigation/link';
import { Pagination } from 'src/components/navigation/pagination';
import { Successful } from 'src/hooks/use-axios';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useModal2 } from 'src/hooks/use-modal';
import { useTitle } from 'src/hooks/use-title';
import { DeleteContactModal } from 'src/pages/settings/organisation/contacts/delete-contact-modal';
import {
  lsoContactTypeLabelsForOverview,
  shipperContactTypeLabels,
  ssoContactTypeLabelsForOverview,
  tsoContactTypeLabelsForOverview,
} from 'src/pages/settings/organisation/contacts/labels';
import { PageParams } from 'src/pages/settings/organisation/contacts/use-page-params';

type Props = {
  contacts: Successful<ContactsRequest>;
  pageParams: PageParams;
};

const Page: FC<Props> = ({ contacts, pageParams }) => {
  const title = 'Contacts';
  useTitle(title);
  return (
    <Stack gap={1}>
      <PageHeader
        title={title}
        {...(contacts.response.data._links.createContact && {
          actions: (
            <Link
              data-testid="create-contact"
              mode="button-primary"
              to="/settings/organisation/contacts/create"
            >
              Create Contact
            </Link>
          ),
        })}
        data-testid="contacts-header"
      />

      {contacts.response.data._embedded?.items.length ? (
        <Stack>
          {contacts.response.data._embedded?.items.map((contact) => (
            <ContactCard
              key={contact.contactId}
              contacts={contacts}
              contact={contact}
              pageParams={pageParams}
            />
          ))}

          <Pagination {...contacts.response.data} configurable={false} />
        </Stack>
      ) : (
        <EmptyCard label="Your organisation has no contact." />
      )}
    </Stack>
  );
};

const ContactCard: FC<{
  contacts: Successful<ContactsRequest>;
  contact: Contact;
  pageParams: PageParams;
}> = ({ contacts, contact, pageParams }) => {
  const { minDesktop } = useBreakpoints();

  const deleteContact = useModal2(
    contact._links.delete
      ? {
          modal: (props) => (
            <DeleteContactModal
              deleteContactUrl={contact._links.delete!.href}
              onSuccess={() => {
                if (
                  contacts.response.data._embedded?.items.length === 1 &&
                  pageParams.value.start > 0
                ) {
                  // deleted last item on page -> go back one page
                  pageParams.set({ start: pageParams.value.start - 1 });
                } else {
                  contacts.refresh?.();
                }
              }}
              {...props}
            />
          ),
          trigger: (props) => (
            <Button
              data-testid={`delete-contact-${contact.contactId}`}
              {...props}
            >
              Delete
            </Button>
          ),
        }
      : null
  );
  return (
    <Card>
      {deleteContact?.modal}

      <Stack flow="column" justifyContent="space-between">
        <p>
          <ExternalLink
            mode="default-underlined"
            href={`mailto:${contact.email}`}
          >
            {contact.email}
          </ExternalLink>
        </p>

        <SplitActions
          mode="button-secondary"
          size="small"
          testId={`user-status-${contact.contactId}`}
        >
          {contact._links.edit && (
            <Link
              data-testid={`edit-contact-${contact.contactId}`}
              to={`/settings/organisation/contacts/${contact.contactId}/edit`}
            >
              Edit
            </Link>
          )}
          {deleteContact?.trigger}
        </SplitActions>
      </Stack>

      <Stack templateColumns={minDesktop ? '1fr 1fr' : '1fr'}>
        <div>
          {contact.firstName || contact.lastName || contact.section ? (
            <p>
              <strong>
                {contact.gender === 'F' && 'Mrs/Ms '}
                {contact.gender === 'M' && 'Mr '}
                {contact.firstName} {contact.lastName}
                {(contact.firstName || contact.lastName) && contact.section
                  ? ', '
                  : null}
                {contact.section}
              </strong>
            </p>
          ) : null}
          <ContactType contact={contact} />
        </div>

        {contact.phone || contact.backupPhone || contact.mobile ? (
          <div>
            {contact.phone && (
              <p>
                Phone: <PhoneLink value={contact.phone} />
              </p>
            )}

            {contact.backupPhone && (
              <p>
                Backup Phone: <PhoneLink value={contact.backupPhone} />
              </p>
            )}

            {contact.mobile && (
              <p>
                Mobile: <PhoneLink value={contact.mobile} />
              </p>
            )}
          </div>
        ) : null}
      </Stack>
    </Card>
  );
};

export const PhoneLink: FC<{ value: PhoneNumber }> = ({ value }) => {
  return (
    <ExternalLink
      mode="default-underlined"
      href={`tel:${value.countryCallingCode}${value.subscriber}`}
    >
      {value.countryCallingCode}
      {value.subscriber}
    </ExternalLink>
  );
};

export const ContactType: FC<{ contact: Contact }> = ({ contact }) => {
  let contacts: string[] = [];

  if (contact.shipperTypes) {
    contact.shipperTypes.map((type) =>
      contacts.push(shipperContactTypeLabels[type])
    );
  }

  if (contact.lsoTypes) {
    contact.lsoTypes.map((type) =>
      contacts.push(lsoContactTypeLabelsForOverview[type])
    );
  }
  if (contact.ssoTypes) {
    contact.ssoTypes.map((type) =>
      contacts.push(ssoContactTypeLabelsForOverview[type])
    );
  }
  if (contact.tsoTypes) {
    contact.tsoTypes.map((type) =>
      contacts.push(tsoContactTypeLabelsForOverview[type])
    );
  }

  if (!contacts.length) {
    return <EmptyValue label="None" />;
  }
  return (
    <p data-testid={`contact-type-${contact.contactId}`}>
      Type:{' '}
      {contacts.length > 0 && contacts.map((contact) => contact).join(', ')}
    </p>
  );
};

export default Page;
